'use client'
import { useImmutableChannel } from '@/api/hooks'
import FileInput from '@/app/components/file-input'
import TrashIcon from '@/app/icons/trash'
import CopyIcon from '@/app/icons/copy'
import { deleteChannel } from '@/app/util/channel-api'
import Button, { SubmitButton } from '@/components/button'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel, IProfileUser } from '@giphy/js-types'
import { usePathname, useRouter } from 'next/navigation'
import { SyntheticEvent, useContext, useEffect, useState } from 'react'
import { ModalContext } from '../../modal'
import PrivateToggle from '../private-toggle'
import { CollectionGif, CollectionLabel, Divider, inputClassName } from '../ui'
import EditCollectionForm from './form'
import { getChannelChildren, updateChannelChildren } from '@/app/(site)/[username]/channel-children'

const channelPlaceholder = {
    display_name: '',
    description: '',
} as Partial<IChannel>

const EditPanel = () => {
    const { user } = useContext(UserContext)
    const { showStatusMessage } = useContext(FlashMessageContext)
    const { modal, setModalType, goBack } = useContext(ModalContext)
    const router = useRouter()
    const loadedChannel: IChannel | undefined = modal?.name === 'editCollection' ? modal.channel : undefined
    const { data: channel = loadedChannel || (channelPlaceholder as IChannel) } = useImmutableChannel(
        loadedChannel ? null : modal?.channelId
    )
    const [displayInput, setDisplayInput] = useState('')
    useEffect(() => {
        if (channel) {
            setDisplayInput(channel?.display_name)
        }
    }, [channel])
    const pathname = usePathname()
    return (
        <EditCollectionForm
            className="flex flex-col items-center gap-2 p-2 font-bold transition-opacity"
            channelId={channel.id}
        >
            <div className={cn('w-40 overflow-hidden rounded-md bg-giphyIndigo')}>
                <CollectionGif gif={channel.featured_gif} />
                <CollectionLabel label={displayInput} />
            </div>
            <label className="self-start text-giphyLightGrey" htmlFor="channel">
                Name
            </label>
            <input
                type="text"
                spellCheck={false}
                value={displayInput}
                placeholder={'Collection Name'}
                name="display_name"
                autoComplete="off"
                onChange={(e) => setDisplayInput(e.target.value)}
                autoCorrect="off"
                className={cn(inputClassName, 'peer/input h-auto w-full py-3.5 text-giphyLightGrey')}
            />
            <div className="flex w-full justify-between text-giphyLightGrey">
                <label>Description</label>
                <label className="self-baseline text-sm text-giphyLightCharcoal">Optional</label>
            </div>
            <textarea
                defaultValue={channel.description}
                maxLength={200}
                placeholder={'Add a brief description about your collection.'}
                name="description"
                className={cn(inputClassName, 'peer/textarea h-20 w-full')}
            />
            <label className="self-start text-giphyLightGrey" htmlFor="collectionId">
                Collection ID
            </label>
            <div className="relative w-full">
                <input
                    id="collectionId"
                    type="text"
                    spellCheck={false}
                    value={channel.id}
                    autoComplete="off"
                    readOnly
                    aria-readonly="true"
                    tabIndex={-1}
                    className={cn(
                        inputClassName,
                        'peer/input h-auto w-full cursor-default bg-giphyDarkCharcoal/40 py-3.5 pr-12 text-giphyLightGrey'
                    )}
                />
                <button
                    type="button"
                    onClick={() => {
                        navigator.clipboard.writeText(String(channel.id))
                        showStatusMessage('Collection ID copied!', 'Failed to copy', true)
                    }}
                    className="absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer p-1 opacity-40 transition-opacity hover:opacity-30"
                    aria-label="Copy Collection ID"
                >
                    <CopyIcon className="size-6 fill-giphyLightGrey" />
                </button>
            </div>
            <div className="mt-3 flex w-full items-start justify-between">
                <div className="">
                    <label className="self-start pb-1 text-giphyLightGrey">Visibility</label>
                    <PrivateToggle initialIsPrivate={channel.is_private} />
                </div>
                <div className="w-1/2">
                    <div className="flex w-full justify-between">
                        <label className="pb-1 text-giphyLightGrey">
                            Banner <span className="text-xs text-giphyDarkCharcoal italic">5 mb max, 1040x160</span>
                        </label>
                        <label className="self-baseline text-sm text-giphyLightCharcoal">Optional</label>
                    </div>
                    <FileInput
                        className="w-full bg-giphyBlack px-4 py-3.5 text-center text-giphyWhite"
                        placeholder="Choose File"
                        accept={['image/*']}
                        name="banner"
                    ></FileInput>
                </div>
            </div>
            <div className="mt-3 flex w-full gap-2">
                <Button
                    type="button"
                    className="flex h-9 flex-1 items-center justify-center rounded-md bg-giphyDarkGrey px-4 py-2 font-bold text-giphyWhite"
                    onClick={() => {
                        setModalType({
                            name: 'createCollection',
                            channelId: channel.id,
                            title: `Create new sub-collection in "${channel.display_name}"`,
                        })
                    }}
                >
                    Create Sub-Collections
                </Button>
                <Button
                    type="button"
                    className="flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-giphyDarkGrey px-4 py-2 font-bold text-giphyWhite"
                    onClick={async (e: SyntheticEvent) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (window.confirm('Are you sure you want to delete this collection?')) {
                            const result = await deleteChannel(channel.id)
                            showStatusMessage('Collection Deleted', 'Failed to delete collection', result.ok)
                            if (result.ok) {
                                const parentsKids = getChannelChildren(channel.parent)
                                if (parentsKids) {
                                    updateChannelChildren(
                                        channel.parent,
                                        parentsKids.filter((c) => c.id !== channel.id)
                                    )
                                }
                                goBack()
                                if (modal?.name === 'editCollection' && modal.channel.url === pathname) {
                                    setTimeout(() => {
                                        if (user) {
                                            router.replace(
                                                (user as unknown as IProfileUser).profile_url || `/${user.username}`
                                            )
                                        }
                                    }, 1000)
                                }
                            }
                        }
                    }}
                >
                    <TrashIcon className="size-4 fill-giphyRed" /> Delete Collection
                </Button>
            </div>
            <input type="hidden" name="user_id" value={user?.id} />
            <input type="hidden" name="id" value={channel?.id} />
            <input type="hidden" name="parent" value={channel?.parent} />
            <Divider />
            <SubmitButton className="w-full cursor-pointer rounded-md bg-gradient-primary px-4 py-3 font-bold text-giphyWhite select-none peer-placeholder-shown/input:pointer-events-none peer-placeholder-shown/input:opacity-50">
                Save Collection
            </SubmitButton>
        </EditCollectionForm>
    )
}
export default EditPanel
